import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, Image as ImageIcon, Upload as UploadIcon } from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../config/firebase';

interface CreatePostDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (content: string, imageUrl?: string) => Promise<void>;
}

export const CreatePostDialog: React.FC<CreatePostDialogProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [content, setContent] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(false);

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
  };

  const handleSubmit = async () => {
    if (!content.trim()) return;

    setIsLoading(true);
    setUploadProgress(true);
    
    try {
      let imageUrl: string | undefined;

      // Upload da imagem se existir
      if (imageFile && user?.uid) {
        const timestamp = Date.now();
        const fileName = `${timestamp}_${imageFile.name}`;
        const storageRef = ref(storage, `posts/${user.uid}/${fileName}`);
        
        await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(storageRef);
      }

      await onSubmit(content, imageUrl);
      
      // Limpar campos
      setContent('');
      setImageFile(null);
      setImagePreview('');
      onClose();
    } catch (error) {
      console.error('Erro ao criar post:', error);
    } finally {
      setIsLoading(false);
      setUploadProgress(false);
    }
  };

  const handleClose = () => {
    setContent('');
    setImageFile(null);
    setImagePreview('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: theme.colors.card,
          color: theme.colors.text.primary,
        }}
      >
        <Typography variant="h6">Criar Post</Typography>
        <IconButton onClick={handleClose} sx={{ color: theme.colors.text.primary }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: theme.colors.card }}>
        <Box sx={{ pt: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="No que você está pensando?"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: theme.colors.text.primary,
                backgroundColor: theme.colors.background,
              },
            }}
          />

          {/* Image Preview */}
          {imagePreview && (
            <Box
              sx={{
                mb: 2,
                position: 'relative',
                width: '100%',
                height: 200,
                borderRadius: theme.radii.small,
                overflow: 'hidden',
                backgroundColor: theme.colors.background,
              }}
            >
              <img
                src={imagePreview}
                alt="Preview"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              <IconButton
                onClick={handleRemoveImage}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: 'rgba(0,0,0,0.6)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.8)',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          )}

          {/* Upload Button */}
          <Button
            variant="outlined"
            component="label"
            startIcon={imageFile ? <ImageIcon /> : <UploadIcon />}
            fullWidth
            sx={{
              color: theme.colors.primary,
              borderColor: theme.colors.primary,
              '&:hover': {
                borderColor: theme.colors.primary,
                backgroundColor: theme.colors.secondary,
              },
            }}
          >
            {imageFile ? 'Trocar Imagem' : 'Adicionar Imagem'}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleImageSelect}
            />
          </Button>

          {uploadProgress && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <CircularProgress size={20} sx={{ mr: 1, color: theme.colors.primary }} />
              <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                Fazendo upload...
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ backgroundColor: theme.colors.card, p: 2 }}>
        <Button
          onClick={handleClose}
          sx={{
            color: theme.colors.text.secondary,
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!content.trim() || isLoading}
          sx={{
            backgroundColor: theme.colors.primary,
            color: 'white',
            '&:hover': {
              backgroundColor: theme.colors.primary,
              opacity: 0.9,
            },
          }}
        >
          {isLoading ? 'Publicando...' : 'Publicar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
